import { useState } from 'react';
import {
  Target, Plus, ExternalLink, Pencil, Trash2, Trophy, Link as LinkIcon, BarChart3,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
} from 'recharts';
import { labApi } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { LabCompetitor } from './types';
import { RADAR_AXES } from './constants';
import { CompetitorModal } from './CompetitorModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface CompetitorSectionProps {
  productId: string;
  competitors: LabCompetitor[];
  onReload: () => void;
}

function getRadarData(scores: LabCompetitor['radarScores']) {
  return RADAR_AXES.map(axis => ({
    axis: axis.label,
    value: scores?.[axis.key as keyof NonNullable<typeof scores>] ?? 0,
  }));
}

function getAverageScore(scores: LabCompetitor['radarScores']): number {
  if (!scores) return 0;
  const values = RADAR_AXES.map(a => scores[a.key as keyof typeof scores] ?? 0);
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}

export function CompetitorSection({ productId, competitors, onReload }: CompetitorSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<LabCompetitor | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleSave = async (data: Record<string, unknown>) => {
    try {
      if (editingCompetitor) {
        await labApi.updateCompetitor(productId, editingCompetitor.id, data);
      } else {
        await labApi.createCompetitor(productId, data);
      }
      setShowModal(false);
      setEditingCompetitor(null);
      onReload();
    } catch (err) {
      console.error('Failed to save competitor:', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await labApi.deleteCompetitor(productId, deleteId);
    setDeleteId(null);
    onReload();
  };

  // Sort by average score for medal assignment
  const ranked = [...competitors].sort((a, b) => getAverageScore(b.radarScores) - getAverageScore(a.radarScores));
  const averageRadarScore = ranked.length
    ? Math.round(ranked.reduce((sum, competitor) => sum + getAverageScore(competitor.radarScores), 0) / ranked.length)
    : 0;
  const strongestCompetitor = ranked[0];
  const trackedLinks = competitors.reduce((sum, competitor) => {
    const coreLinks = [
      competitor.linkAdLibrary,
      competitor.linkLanding,
      competitor.linkAdvertorial,
      competitor.linkQuiz,
      competitor.linkListicale,
    ].filter(Boolean).length;
    return sum + coreLinks + (competitor.otherLinks?.length || 0);
  }, 0);
  const trackedAds = competitors.reduce((sum, competitor) => sum + (competitor.topAds?.length || 0), 0);

  const LINK_COLS = [
    { key: 'linkAdLibrary', label: 'Bibl. reklam' },
    { key: 'linkLanding', label: 'Landing' },
    { key: 'linkAdvertorial', label: 'Advertorial' },
    { key: 'linkQuiz', label: 'Quiz' },
    { key: 'linkListicale', label: 'Listicale' },
  ] as const;

  return (
    <section className="space-y-5">
      <Card padding="md" className="bg-accent-primary/5 border-accent-primary/20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-card bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center shrink-0">
              <Target className="w-6 h-6 text-accent-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Radar konkurencji</h2>
              <p className="text-sm text-text-muted mt-1 max-w-2xl">
                Porównanie konkurentów, aktywnych źródeł reklamowych i najmocniejszych kreacji dla tego produktu.
              </p>
            </div>
          </div>
          <Button
            onClick={() => { setEditingCompetitor(null); setShowModal(true); }}
            leftIcon={<Plus />}
          >
            Dodaj konkurenta
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Konkurenci</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{competitors.length}</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Średni radar</span>
            <strong className="block text-2xl font-semibold text-accent-primary mt-1">{averageRadarScore}/100</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Śledzone linki</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{trackedLinks}</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Top reklamy</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{trackedAds}</strong>
          </div>
        </div>
      </Card>

      {competitors.length === 0 ? (
        <EmptyState
          icon={Target}
          title="Brak konkurentów"
          description="Dodaj pierwszego konkurenta, aby zobaczyć radar konkurencji."
          buttonLabel="Dodaj konkurenta"
          onButtonClick={() => { setEditingCompetitor(null); setShowModal(true); }}
          buttonIcon={Plus}
          size="sm"
        />
      ) : (
        <div className="space-y-6">
          {strongestCompetitor && (
            <div className="flex items-center gap-2 rounded-card border border-success-border bg-success-bg px-4 py-3">
              <Trophy className="w-4 h-4 text-success shrink-0" />
              <p className="text-sm text-text-secondary">
                Najmocniejszy konkurent w radarze: <span className="font-semibold text-text-primary">{strongestCompetitor.name}</span>
                <span className="text-text-muted"> ({getAverageScore(strongestCompetitor.radarScores)}/100)</span>
              </p>
            </div>
          )}

          {/* Radar cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ranked.map((c, idx) => {
              const avgScore = getAverageScore(c.radarScores);
              const radarData = getRadarData(c.radarScores);

              return (
                <Card
                  key={c.id}
                  padding="md"
                  className="hover:border-accent-primary/30"
                >
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant={idx === 0 ? 'success' : idx === 1 ? 'info' : idx === 2 ? 'warning' : 'neutral'} size="sm">
                      #{idx + 1}
                    </Badge>
                    <h4 className="text-sm font-medium text-text-primary flex-1 truncate">{c.name}</h4>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="icon"
                        size="sm"
                        onClick={() => { setEditingCompetitor(c); setShowModal(true); }}
                        aria-label={`Edytuj konkurenta ${c.name}`}
                        leftIcon={<Pencil />}
                      />
                      <Button
                        variant="icon"
                        size="sm"
                        onClick={() => setDeleteId(c.id)}
                        aria-label={`Usuń konkurenta ${c.name}`}
                        leftIcon={<Trash2 />}
                      />
                    </div>
                  </div>

                  {/* Radar Chart */}
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                        <PolarGrid stroke="var(--border-default)" />
                        <PolarAngleAxis
                          dataKey="axis"
                          tick={{ fill: 'rgb(var(--text-muted))', fontSize: 10 }}
                        />
                        <Radar
                          dataKey="value"
                          fill="rgb(var(--accent-primary) / 0.15)"
                          stroke="rgb(var(--accent-primary))"
                          strokeWidth={1.5}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Average score */}
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-xs text-text-muted">Wynik ogólny:</span>
                    <span className={`text-xl font-bold ${avgScore >= 70 ? 'text-success' : avgScore >= 40 ? 'text-warning' : 'text-text-secondary'}`}>
                      {avgScore}
                    </span>
                    <span className="text-xs text-text-disabled">/100</span>
                  </div>

                  {/* Top Ads */}
                  {c.topAds && c.topAds.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border-subtle">
                      <span className="text-[10px] text-text-muted uppercase tracking-wide mb-1.5 block">Top reklamy</span>
                      {c.topAds.slice(0, 3).map((ad, adIdx) => (
                        <div key={adIdx} className="flex items-center gap-2 text-xs py-1">
                          <span className="text-text-primary truncate flex-1">{ad.title}</span>
                          <span className="text-text-muted shrink-0">{ad.activeDays}d</span>
                          {ad.url && (
                            <a href={ad.url} target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:opacity-80 shrink-0">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Competitor table */}
          <div className="bg-surface-raised rounded-card border border-border overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-accent-primary" />
                <h3 className="text-sm font-semibold text-text-primary">Źródła konkurencji</h3>
              </div>
              <Badge variant="neutral" size="sm" leftIcon={<LinkIcon />}>{trackedLinks} linków</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-left px-4 py-3 text-xs text-text-muted font-medium">Nazwa</th>
                    {LINK_COLS.map(col => (
                      <th key={col.key} className="text-center px-3 py-3 text-xs text-text-muted font-medium">{col.label}</th>
                    ))}
                    <th className="text-center px-3 py-3 text-xs text-text-muted font-medium">Inne</th>
                    <th className="text-right px-4 py-3 text-xs text-text-muted font-medium">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map(c => (
                    <tr key={c.id} className="border-b border-border-subtle last:border-0 hover:bg-surface-hover/30">
                      <td className="px-4 py-3 text-text-primary font-medium">{c.name}</td>
                      {LINK_COLS.map(col => {
                        const url = c[col.key as keyof LabCompetitor] as string | null | undefined;
                        return (
                          <td key={col.key} className="text-center px-3 py-3">
                            {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex text-accent-primary hover:opacity-80">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          ) : (
                              <span className="text-text-disabled">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-3 py-3">
                        {c.otherLinks && c.otherLinks.length > 0 ? (
                          <span className="text-xs text-text-secondary">{c.otherLinks.length}</span>
                        ) : (
                          <span className="text-text-disabled">-</span>
                        )}
                      </td>
                      <td className="text-right px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="icon"
                            size="sm"
                            onClick={() => { setEditingCompetitor(c); setShowModal(true); }}
                            aria-label={`Edytuj konkurenta ${c.name}`}
                            leftIcon={<Pencil />}
                          />
                          <Button
                            variant="icon"
                            size="sm"
                            onClick={() => setDeleteId(c.id)}
                            aria-label={`Usuń konkurenta ${c.name}`}
                            leftIcon={<Trash2 />}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <CompetitorModal
          productId={productId}
          competitor={editingCompetitor}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingCompetitor(null); }}
        />
      )}

      {deleteId && (
        <DeleteConfirmModal
          title="Usuń konkurenta"
          message="Czy na pewno chcesz usunąć tego konkurenta? Tej operacji nie można cofnąć."
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
        />
      )}
    </section>
  );
}
